import {DOCUMENT_TYPE} from "utils/constants";
import {formatDate, formatDateTime, formatAmountValue} from "utils/valueFormatter.function";
import {approvableKeys, fromValue} from "./tasksGrouping.function";
import {normalizeTaskAmount, sumTotalAmount} from "./taskAmounts.function";
import {taskDescription} from "./taskDescription.function";
import type {TaskGroup} from "./tasksGrouping.function";
import type {CurrencyTotal} from "./taskAmounts.function";
import type {MyTask, TaskWrapper, Translate} from "types/myTasks";

// grouping needs it too, so it lives with the other pure task helpers
export {fromValue};

/**
 * Column + row model for the ag-grid based my-tasks list.
 * Pure functions, so the whole column layout can be unit-tested without mounting the grid.
 */

export const CELL_TYPE = Object.freeze({
    TEXT: 'text',
    NUMBER: 'number',
    AMOUNT: 'amount',
    DATE: 'date',
    DATETIME: 'datetime',
    BOOL: 'bool',
    DOC_TYPE: 'docType',
    LIST: 'list',
    STATE: 'state'
});

export const ROW_TYPE = Object.freeze({TASK: 'task', GROUP: 'group'});

export interface FieldMeta {
    type: string;
    labelKey: string;
    width: number;
    flex?: number;
}

/**
 * One row of the flat list ag-grid renders: a group header, or a task under it. Which fields
 * are set follows `rowType`.
 */
export interface GridRow {
    rowType: string;
    groupKey?: string;
    label?: string;
    count?: number;
    totals?: CurrencyTotal[];
    approvable?: string[];
    wrappers?: TaskWrapper[];
    collapsed?: boolean;
    // a group row heading the tasks the user sent on review
    onReview?: boolean;
    task?: MyTask;
    selected?: boolean;
}

// only the part of an ag-grid row node the regrouping reads, so plain rows can stand in for it
interface SortedRowNode {
    data?: GridRow;
}

// synthetic column: the "From" value TaskView derives from supplier/requester fields
export const FROM_FIELD = '__from';

// synthetic column: the states the flat list draws as icons in its actions column
export const STATE_FIELD = '__state';

const ASSIGNMENT_STATES: Readonly<Record<string, string>> = Object.freeze({
    REVIEW: 'review',
    FORWARDED: 'forwarded',
    REASSIGNED: 'reassigned'
});

// this grid spells the states out rather than drawing the flat list's icons, so it words them
// as what they mean for the user. A state with no wording of its own keeps the icon's tooltip
const GRID_ASSIGNMENT_LABELS: Readonly<Record<string, string>> = Object.freeze({
    substitute: 'myTasks.grid.assignment.substitute',
    forwarded: 'myTasks.grid.assignment.forwarded',
    reassigned: 'myTasks.grid.assignment.reassigned'
});

/**
 * The label one state reads as in the grid's assignment cell.
 */
export function stateLabelKey(state: string): string {
    return GRID_ASSIGNMENT_LABELS[state] ?? 'myTasks.taskIcon.' + state;
}

/**
 * The task's states, as the keys their labels are translated under. A substitute task can carry
 * an assignment type as well, which is why the flat list can show two icons at once.
 */
export function stateKeys(task: MyTask): string[] {
    if (!task) return [];
    const keys = task.actorType === 'SUBSTITUTE' ? ['substitute'] : [];
    const assignment = ASSIGNMENT_STATES[task.taskAssignmentType];
    if (assignment) keys.push(assignment);
    return keys;
}

/**
 * The task fields that can become columns, in the order they should appear. This map gates
 * visibility: a field the API returns but that is not listed here gets no column, so new or
 * internal API fields never leak into the grid on their own.
 */
export const FIELD_META: Readonly<Record<string, FieldMeta>> = Object.freeze({
    documentType: {type: CELL_TYPE.DOC_TYPE, labelKey: 'myTasks.column.documentType', width: 70},
    [FROM_FIELD]: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.column.supplierName', width: 180},
    description: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.column.description', width: 210, flex: 2},
    companyName: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.column.companyName', width: 160},
    documentDueDate: {type: CELL_TYPE.DATE, labelKey: 'myTasks.grid.column.documentDueDate', width: 130},
    amount: {type: CELL_TYPE.AMOUNT, labelKey: 'myTasks.column.amount', width: 150},
    currency: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.grid.column.currency', width: 90},
    numberOfComments: {type: CELL_TYPE.NUMBER, labelKey: 'myTasks.column.numberOfComments', width: 60},
    [STATE_FIELD]: {type: CELL_TYPE.STATE, labelKey: 'myTasks.column.assignment', width: 120},
    dueDate: {type: CELL_TYPE.DATE, labelKey: 'myTasks.column.dueDate', width: 120},
    activatedDate: {type: CELL_TYPE.DATE, labelKey: 'myTasks.grid.column.activatedDate', width: 130},
    invoiceDate: {type: CELL_TYPE.DATE, labelKey: 'myTasks.column.invoiceDate', width: 120},
    createdDate: {type: CELL_TYPE.DATE, labelKey: 'myTasks.column.createdDate', width: 120},
    lastChangedByUserName: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.column.lastChangedBy', width: 160},

    // remaining API fields - shown in the comfortable view
    id: {type: CELL_TYPE.NUMBER, labelKey: 'myTasks.overview.id', width: 100},
    displayId: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.grid.column.displayId', width: 110},
    supplierName: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.grid.column.supplierName', width: 160},
    requesterName: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.grid.column.requesterName', width: 160},
    companyId: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.grid.column.companyId', width: 120},
    // as wide as Amount: it is shown the same way, currency and all
    foreignAmount: {type: CELL_TYPE.AMOUNT, labelKey: 'myTasks.grid.column.foreignAmount', width: 136},
    originalAssignee: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.grid.column.originalAssignee', width: 160},
    // postponedComment is left out on purpose: it is no column the list offers
    //
    // the display name the API resolves; the raw externalApplicationName is an internal id
    displayApplicationTypeName: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.grid.column.externalApplicationName', width: 170},
    externalId: {type: CELL_TYPE.TEXT, labelKey: 'myTasks.grid.column.externalId', width: 120},
    idProcess: {type: CELL_TYPE.NUMBER, labelKey: 'myTasks.grid.column.idProcess', width: 120},
});

/**
 * "Amount" already renders the currency next to the value, so a standalone currency column
 * would just repeat it. The foreign currency has no entry at all: "Foreign amount" carries it.
 */
const MERGED_INTO_AMOUNT: readonly string[] = Object.freeze(['currency']);

/**
 * The columns the grid can show, in order: every FIELD_META entry except the ones another
 * cell already renders. Fixed - it does not follow the fields the current data happens to
 * carry, so the column set stays the same from one load to the next.
 */
export const COLUMN_FIELDS: readonly string[] = Object.freeze(
    Object.keys(FIELD_META).filter(f => !MERGED_INTO_AMOUNT.includes(f))
);

/**
 * Technical ids: the picker still offers them, they are just off until the user asks for them.
 */
const HIDDEN_BY_DEFAULT: readonly string[] = Object.freeze(['id', 'idProcess', 'companyId']);

/**
 * The columns shown to a user who has not picked their own set.
 */
export const DEFAULT_COLUMN_FIELDS: readonly string[] = Object.freeze(
    COLUMN_FIELDS.filter(f => !HIDDEN_BY_DEFAULT.includes(f))
);

/**
 * The columns actually rendered: the default set until the user picks their own, then their
 * selection - always in the canonical column order.
 */
export function resolveVisibleFields(selected: string[] | null): string[] {
    if (!selected) return [...DEFAULT_COLUMN_FIELDS];

    const chosen = new Set(selected);
    const visible = COLUMN_FIELDS.filter(f => chosen.has(f));
    return visible.length > 0 ? visible : [...DEFAULT_COLUMN_FIELDS];
}

/**
 * add or remove one field from the current selection, keeping the canonical order.
 * Removing the last remaining column is refused - an empty grid shows nothing at all.
 */
export function toggleField(visibleFields: string[], field: string): string[] {
    const chosen = new Set(visibleFields);
    if (chosen.has(field)) {
        if (chosen.size <= 1) return [...visibleFields];
        chosen.delete(field);
    } else {
        chosen.add(field);
    }
    return COLUMN_FIELDS.filter(f => chosen.has(f));
}

export const COLUMNS_STORAGE_KEY = '@@myTasks/gridColumns';

/**
 * Columns renamed after a selection could already have been stored. A stored name is read as
 * the one that replaced it, so the rename does not silently drop the column from a saved set -
 * or, where it was the only one saved, throw the user back to every column at once.
 */
const RENAMED_FIELDS: Readonly<Record<string, string>> = Object.freeze({
    externalApplicationName: 'displayApplicationTypeName'
});

/**
 * the picked columns survive leaving the page; a missing or unreadable entry just means
 * "show the default columns"
 */
export function readStoredColumns(storage: Storage = window.localStorage): string[] | null {
    try {
        const raw = storage.getItem(COLUMNS_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.every(f => typeof f === 'string') && parsed.length > 0
            ? [...new Set(parsed.map((f: string) => RENAMED_FIELDS[f] ?? f))]
            : null;
    } catch (e) {
        return null;
    }
}

export function writeStoredColumns(fields: string[] | null, storage: Storage = window.localStorage): void {
    try {
        if (fields === null) storage.removeItem(COLUMNS_STORAGE_KEY);
        else storage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(fields));
    } catch (e) {
        // a full or blocked storage must not break the grid
    }
}

export const LAYOUT_STORAGE_KEY = '@@myTasks/gridLayout';

/**
 * What the user did to the columns themselves - the order they dragged them into and the widths
 * they dragged them to. Kept apart from the picked column set: a column switched off keeps the
 * width it was given, for when it comes back.
 */
export interface ColumnLayout {
    order: string[];
    widths: Record<string, number>;
}

/**
 * the dragged order and widths survive leaving the page; anything unreadable just means
 * "lay the columns out the way they come"
 */
export function readStoredLayout(storage: Storage = window.localStorage): ColumnLayout | null {
    try {
        const raw = storage.getItem(LAYOUT_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);

        // a rename can map two stored names onto one, and duplicate ids would give the grid duplicate columns
        const order: string[] = Array.isArray(parsed?.order)
            ? [...new Set<string>(parsed.order.filter((f: unknown) => typeof f === 'string')
                .map((f: string) => RENAMED_FIELDS[f] ?? f))]
            : [];
        const widths: Record<string, number> = {};
        for (const [field, width] of Object.entries(parsed?.widths ?? {})) {
            if (typeof width === 'number' && width > 0) widths[RENAMED_FIELDS[field] ?? field] = width;
        }

        return order.length > 0 || Object.keys(widths).length > 0 ? {order, widths} : null;
    } catch (e) {
        return null;
    }
}

export function writeStoredLayout(layout: ColumnLayout | null, storage: Storage = window.localStorage): void {
    try {
        if (layout === null) storage.removeItem(LAYOUT_STORAGE_KEY);
        else storage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
    } catch (e) {
        // a full or blocked storage must not break the grid
    }
}

export const SORT_STORAGE_KEY = '@@myTasks/gridSort';

/**
 * One column the list is sorted by, as ag-grid reports it. `sortIndex` orders the columns
 * against each other when more than one is sorted at a time.
 */
export interface SortedColumn {
    colId: string;
    sort: 'asc' | 'desc';
    sortIndex?: number;
}

/**
 * the column the user sorted by survives leaving the page - opening a task and coming back
 * included; anything unreadable just means "sort the way the list opens"
 */
export function readStoredSort(storage: Storage = window.localStorage): SortedColumn[] | null {
    try {
        const raw = storage.getItem(SORT_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        const sorted = parsed
            .filter(entry => typeof entry?.colId === 'string' && (entry.sort === 'asc' || entry.sort === 'desc'))
            .map(entry => ({
                colId: RENAMED_FIELDS[entry.colId] ?? entry.colId,
                sort: entry.sort as 'asc' | 'desc',
                sortIndex: typeof entry.sortIndex === 'number' ? entry.sortIndex : undefined
            }));
        return sorted.length > 0 ? sorted : null;
    } catch (e) {
        return null;
    }
}

export function writeStoredSort(sorted: SortedColumn[] | null, storage: Storage = window.localStorage): void {
    try {
        if (sorted === null || sorted.length === 0) storage.removeItem(SORT_STORAGE_KEY);
        else storage.setItem(SORT_STORAGE_KEY, JSON.stringify(sorted));
    } catch (e) {
        // a full or blocked storage must not break the grid
    }
}

/**
 * The order the list reads in on screen, top to bottom: the groups in their own order, and inside
 * each of them the order the grid sorted its rows into - `sortedKeys`. A collapsed group has no
 * rows in the grid, so its tasks keep the order they came in, in the group's own place.
 */
export function visibleTaskOrder(groups: TaskGroup[], sortedKeys: readonly string[]): string[] {
    const rank = new Map(sortedKeys.map((key, index) => [key, index]));
    const order: string[] = [];
    groups.forEach(group => {
        const keys = group.wrappers.map(wrapper => wrapper.task.key);
        if (keys.some(key => rank.has(key))) {
            keys.sort((first, second) => (rank.get(first) ?? Number.MAX_SAFE_INTEGER) - (rank.get(second) ?? Number.MAX_SAFE_INTEGER));
        }
        order.push(...keys);
    });
    return order;
}

/**
 * The visible columns in the order the user dragged them into. A column switched on after that
 * order was stored is missing from it, and goes back where the canonical order has it: right
 * behind the column it follows there.
 */
export function orderVisibleFields(visibleFields: string[], order?: readonly string[] | null): string[] {
    if (!order || order.length === 0) return [...visibleFields];

    const ordered = order.filter(field => visibleFields.includes(field));
    for (const field of visibleFields) {
        if (ordered.includes(field)) continue;
        const precedes = visibleFields.slice(0, visibleFields.indexOf(field))
            .reverse()
            .find(earlier => ordered.includes(earlier));
        ordered.splice(precedes === undefined ? 0 : ordered.indexOf(precedes) + 1, 0, field);
    }
    return ordered;
}

export function fieldLabel(field: string, translate: Translate): string {
    const meta = FIELD_META[field];
    return meta ? translate(meta.labelKey) : field;
}

export function rawValue(task: MyTask, field: string): any {
    if (!task) return null;
    if (field === FROM_FIELD) return fromValue(task);
    if (field === STATE_FIELD) return stateKeys(task);
    return task[field];
}

/**
 * value used for sorting and for the grid's quick filter - always a primitive
 */
export function sortValue(task: MyTask, field: string): string | number | boolean | null {
    const value = rawValue(task, field);
    const type = FIELD_META[field]?.type;
    if (type === CELL_TYPE.DOC_TYPE) return DOCUMENT_TYPE.asString(value);
    if (value && typeof value === 'object') return displayValue(task, field);
    return value ?? null;
}

/**
 * printable representation of a field, used by the default cell renderer and CSV export
 */
export function displayValue(task: MyTask, field: string, translate: Translate = (k) => k): string {
    // shown with the leave period, as the flat list does; sorting keeps the bare text
    if (field === 'description' && task) return taskDescription(task);
    // the reviewer's task carries the reviewer's own assignment; what the sender wants to know is who has it
    if (field === STATE_FIELD && task?.isOnReview) {
        return task.currentAssignee
            ? translate('myTasks.onReview.waitingFor', task.currentAssignee)
            : translate('myTasks.onReview.waiting');
    }
    const value = rawValue(task, field);
    if (value === null || value === undefined || value === '') return '';

    switch (FIELD_META[field]?.type) {
        case CELL_TYPE.DOC_TYPE:
            return translate('documentType.' + DOCUMENT_TYPE.asString(value));
        case CELL_TYPE.DATE:
            return formatDate(value, '');
        case CELL_TYPE.DATETIME:
            return formatDateTime(value, '');
        case CELL_TYPE.AMOUNT:
            return formatAmountValue(value);
        case CELL_TYPE.BOOL:
            return value ? translate('myTasks.grid.yes') : translate('myTasks.grid.no');
        case CELL_TYPE.STATE:
            // the flat list shows these as icons; here they are the words those icons stand for
            return (value as string[]).map(key => translate(stateLabelKey(key))).join(', ');
        case CELL_TYPE.LIST:
            return Array.isArray(value) ? value.join(', ') : String(value);
        default:
            return unknownFieldValue(value, translate);
    }
}

/**
 * a field the grid knows nothing about: whatever the API gave us, rendered without crashing the grid
 */
function unknownFieldValue(value: any, translate: Translate): string {
    if (typeof value === 'boolean') return value ? translate('myTasks.grid.yes') : translate('myTasks.grid.no');
    if (Array.isArray(value)) return value.map(v => (v && typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
    if (typeof value === 'object') return value.name ?? JSON.stringify(value);
    return String(value);
}

// Excel and Sheets run a cell that opens with one of these as a formula
const CSV_FORMULA_START = /^[=+\-@\t\r]/;

/**
 * a cell as it is safe to write to the file. A negative amount is left alone: quoting it would
 * cost the sheet a number, and a number is never read as a formula anyway.
 */
export function csvSafeValue(value: string): string {
    return CSV_FORMULA_START.test(value) && !Number.isFinite(Number(value)) ? "'" + value : value;
}

/**
 * CSV-only columns: the grid shows each amount with its unit in one cell, a spreadsheet wants
 * them apart. Keyed by the amount column each one follows.
 */
export const CSV_UNIT_COLUMNS: Readonly<Record<string, {colId: string; labelKey: string}>> = Object.freeze({
    amount: {colId: 'csvCurrency', labelKey: 'myTasks.grid.column.currency'},
    foreignAmount: {colId: 'csvForeignCurrency', labelKey: 'myTasks.grid.column.foreignCurrency'}
});

/**
 * the exported columns: the displayed ones, each amount followed by its unit column
 */
export function csvColumnIds(displayed: readonly string[]): string[] {
    return displayed.flatMap(colId => (CSV_UNIT_COLUMNS[colId] ? [colId, CSV_UNIT_COLUMNS[colId].colId] : [colId]));
}

/**
 * a cell of the export. The amount pairs are read the way the amount cells show them, so a leave
 * request exports its days with "days" and a repeated foreign amount stays empty.
 */
export function csvValue(task: MyTask, colId: string, translate: Translate): string {
    if (!task) return '';
    const amounts = () => normalizeTaskAmount(task, translate);
    switch (colId) {
        case 'amount':
            return amounts().amount;
        case CSV_UNIT_COLUMNS.amount.colId:
            return amounts().unit ?? '';
        case 'foreignAmount':
            return amounts().foreignAmount ?? '';
        case CSV_UNIT_COLUMNS.foreignAmount.colId:
            return amounts().foreignCurrency ?? '';
        default:
            return displayValue(task, colId, translate);
    }
}

/**
 * the download's name, stamped with the day it was taken so one export does not overwrite another
 */
export function csvFileName(now: number = Date.now()): string {
    const date = new Date(now);
    const pad = (value: number) => String(value).padStart(2, '0');
    return 'my-tasks-' + date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + '.csv';
}

/**
 * flatten the grouped wrappers into grid rows, inserting a full-width row per group.
 * Collapsed groups keep their header row but drop their tasks; a headless group has no header row.
 */
export function buildRowData(groups: TaskGroup[], collapsedGroups: Record<string, boolean> = {},
                             translate: Translate = (k) => k): GridRow[] {
    const rows: GridRow[] = [];
    groups.forEach(group => {
        if (group.headless) {
            group.wrappers.forEach(wrapper => rows.push({
                rowType: ROW_TYPE.TASK,
                groupKey: group.key,
                task: wrapper.task,
                selected: wrapper.selected
            }));
            return;
        }
        const label = group.labelKey ? translate(group.labelKey) : group.label;
        rows.push({
            rowType: ROW_TYPE.GROUP,
            groupKey: group.key,
            label,
            count: group.wrappers.length,
            // the reviews are not the user's to approve, so they add nothing to what is pending
            totals: group.onReview ? [] : sumTotalAmount(group.wrappers),
            approvable: group.onReview ? [] : approvableKeys(group.wrappers),
            wrappers: group.wrappers,
            collapsed: Boolean(collapsedGroups[group.key]),
            onReview: Boolean(group.onReview)
        });
        if (!collapsedGroups[group.key]) {
            group.wrappers.forEach(wrapper => rows.push({
                rowType: ROW_TYPE.TASK,
                // the group a task belongs to survives sorting, so rows can be regrouped after it
                groupKey: group.key,
                task: wrapper.task,
                selected: wrapper.selected
            }));
        }
    });
    return rows;
}

export function getRowId(row: GridRow): string {
    if (row.rowType === ROW_TYPE.GROUP) return 'group:' + row.groupKey;
    // a review task is someone else's, so its id is kept apart from the user's own
    return row.task.isOnReview ? row.task.key : 'task:' + row.task.id;
}

/**
 * Restore the grouping after the grid sorted the flat row list.
 *
 * Sorting moves every row, which would otherwise scatter the full-width group rows.
 * This puts each group row back in front of its own tasks, keeping the groups in their
 * original order while preserving the sort order the grid produced inside each group.
 * Mutates `nodes` in place, as postSortRows requires.
 */
export function regroupSortedNodes(nodes: SortedRowNode[], groupOrder: string[]): void {
    if (!nodes || groupOrder.length === 0) return;

    const groupRows = new Map<string, SortedRowNode>();
    const tasksByGroup = new Map<string, SortedRowNode[]>(groupOrder.map(key => [key, []]));

    nodes.forEach(node => {
        const row = node.data;
        if (!row) return;
        if (row.rowType === ROW_TYPE.GROUP) groupRows.set(row.groupKey, node);
        else if (tasksByGroup.has(row.groupKey)) tasksByGroup.get(row.groupKey).push(node);
    });

    const rebuilt: SortedRowNode[] = [];
    groupOrder.forEach(key => {
        const groupRow = groupRows.get(key);
        if (groupRow) rebuilt.push(groupRow);
        rebuilt.push(...tasksByGroup.get(key));
    });

    // only rewrite when nothing was dropped, so an unexpected row shape cannot lose rows
    if (rebuilt.length !== nodes.length) return;
    nodes.length = 0;
    nodes.push(...rebuilt);
}
